import * as React from 'react';
import { Site } from 'sections/client-management/entities';
import {
  ILinkConfig,
  ILinkLookup,
  IPrecompiledLink,
  ITransformedLink,
} from 'sections/design/template-management/entities/link-config';
import { ITemplate, ITemplateHistoryItem } from 'entities/template/template';
import {
  ITemplateValidationCounts,
  ITemplateValidationRule,
} from 'sections/design/template-management/entities/templates';
import {
  TemplateEditorTabs,
  TEMPLATE_EDITOR_DEFAULT_PERCENTAGE,
  TEMPLATE_EDITOR_DESKTOP_SIZE_STORAGE_ID,
  TEMPLATE_EDITOR_MOBILE_SIZE_STORAGE_ID,
  TEMPLATE_EDITOR_THEME_STORAGE_ID,
  TEMPLATE_EDITOR_WORD_WRAP_STORAGE_ID,
} from '../constants';
import { IValue as ImportedValue, TemplateEditorProvider } from './TemplateEditorProvider';
import { ActionTypes, TemplateEditorReducer } from './TemplateEditorReducer';
import { getSampleCompilationContext } from './sample-compilation-context';
export type IValue = ImportedValue;

export interface IAction {
  type: ActionTypes;
  payload?: any;
}

export type Dispatch = (action: IAction) => void;

export interface IMultiCursorModifier {
  control: string;
  alt: string;
}

export interface IEditorSettings {
  theme: string;
  wordWrap: string;
  desktopSize: string;
  mobileSize: string;
  multiCursorModifier?: IMultiCursorModifier;
  readOnly?: boolean;
}

export interface IState {
  activeLeftTab: TemplateEditorTabs;
  activeRightTab: TemplateEditorTabs;
  rightWindowPercentage: number;
  editorSettings: IEditorSettings;
  originalTemplate?: ITemplate;
  template?: ITemplate;
  templateHistory: ITemplateHistoryItem[];
  lastCompiledTemplateBody: string;
  language: string;
  languageStatus: string;
  linkLookups: ILinkLookup[];
  linkConfigs: ILinkConfig[];
  site?: Site;
  lastCompiledCompilationContext: any;
  compilationContext: any;
  randomCompilationContexts: any[];
  randomCompilationContextIndex: number;
  compiledTemplate: string;
  precompiledLinks: IPrecompiledLink[];
  transformedLinks: ITransformedLink[];
  initialPreviewRan: boolean;
  validationCounts?: ITemplateValidationCounts;
  validationResults: ITemplateValidationRule[];
  snapshotValid: boolean;
  hasUnsavedChanges: boolean;
  requiresPreview: boolean;
  editingInTab: boolean;
  editingDone: boolean;
  onsiteValidationMessage?: string;
  previewingVersion: string;
}

export const defaultContextValues: IState = {
  activeLeftTab: TemplateEditorTabs.templateInput,
  activeRightTab: TemplateEditorTabs.templatePreview,
  rightWindowPercentage: TEMPLATE_EDITOR_DEFAULT_PERCENTAGE,
  editorSettings: {
    theme: localStorage.getItem(TEMPLATE_EDITOR_THEME_STORAGE_ID) ?? 'vs-dark',
    wordWrap: localStorage.getItem(TEMPLATE_EDITOR_WORD_WRAP_STORAGE_ID) ?? 'off',
    desktopSize: localStorage.getItem(TEMPLATE_EDITOR_DESKTOP_SIZE_STORAGE_ID) ?? '910',
    mobileSize: localStorage.getItem(TEMPLATE_EDITOR_MOBILE_SIZE_STORAGE_ID) ?? '320',
    multiCursorModifier: {
      control: 'ctrlCmd',
      alt: 'alt',
    },
    readOnly: false,
  },
  originalTemplate: {
    id: '',
    siteId: '',
    template: '',
    channel: '',
    templateName: '',
    type: '',
    isEmbedded: false,
    enrichmentConfigIds: [],
    audit: {
      comment: '',
      createdAt: '',
      createdBy: '',
      lastUpdatedAt: '',
      lastUpdatedBy: '',
      s3VersionId: '',
    },
    supportedLanguages: [],
  },
  template: {
    id: '',
    siteId: '',
    template: '',
    channel: '',
    templateName: '',
    type: '',
    isEmbedded: false,
    enrichmentConfigIds: [],
    audit: {
      comment: '',
      createdAt: '',
      createdBy: '',
      lastUpdatedAt: '',
      lastUpdatedBy: '',
      s3VersionId: '',
    },
    supportedLanguages: [],
  },
  templateHistory: [],
  lastCompiledTemplateBody: '',
  language: '',
  linkLookups: [],
  linkConfigs: [],
  lastCompiledCompilationContext: {
    compilationContext: {
      webviewUrl: 'fakeurl.com',
      unsubscribeUrl: 'fakeurl.com',
    },
  },
  compilationContext: {
    compilationContext: getSampleCompilationContext(),
  },
  randomCompilationContexts: [],
  randomCompilationContextIndex: -1,
  compiledTemplate: '',
  precompiledLinks: [],
  transformedLinks: [],
  initialPreviewRan: false,
  validationCounts: {
    fail: 0,
    pass: 0,
    warning: 0,
  },
  validationResults: [],
  snapshotValid: true,
  hasUnsavedChanges: false,
  requiresPreview: false,
  languageStatus: 'DRAFT',
  editingInTab: false,
  editingDone: false,
  previewingVersion: '',
};

export const TemplateEditorStateContext = React.createContext<IState | undefined>(undefined);
export const TemplateEditorDispatchContext = React.createContext<Dispatch | undefined>(undefined);

function useTemplateEditorState() {
  const context = React.useContext(TemplateEditorStateContext);
  if (context === undefined) {
    throw new Error('useTemplateEditorState must be used within a TemplateEditorProvider');
  }
  return context;
}

function useTemplateEditorDispatch() {
  const context = React.useContext(TemplateEditorDispatchContext);
  if (context === undefined) {
    throw new Error('useTemplateEditorDispatch must be used within a TemplateEditorProvider');
  }
  return context;
}

// Tests if editor is in PREVIEW MODE, based on state
function isPreviewMode(state: IState | string): boolean {
  let verId = '';
  if (typeof state === 'object' && 'previewingVersion' in state) {
    verId = state.previewingVersion ?? '';
  }
  if (typeof state === 'string') {
    verId = state as string;
  }
  return verId.length > 0;
}

export {
  TemplateEditorProvider,
  TemplateEditorReducer,
  useTemplateEditorState,
  useTemplateEditorDispatch,
  isPreviewMode,
};
